import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FaTwitter, FaInstagram, FaFacebookF } from 'react-icons/fa';
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
  useInView,
} from 'framer-motion';

import '../styles/HomePage.css';

import BookSection from '../components/BookSection';
import NewsletterSignup from '../components/NewsLetterForm';
import ReviewTicker from '../components/ReviewTicker';
import AboutSection from '../components/AboutSection';
// import UpcomingBookSection from '../components/UpcomingBooksSection';

// ──────────────────────────────
// Data & SEO config
// ──────────────────────────────

const heroContentVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.05,
      delayChildren: 0.25,
    },
  },
};

const authorData = {
  name: 'Robert Falkenberg',
  titles: ['Author', 'Legal Professional', 'Family Man'],
  socials: [
    {
      name: 'Twitter',
      icon: <FaTwitter />,
      url: 'https://twitter.com/RobertFalkenbe6',
    },
    {
      name: 'Instagram',
      icon: <FaInstagram />,
      url: 'https://www.instagram.com/robertfalkenbergauthor/',
    },
    {
      name: 'Facebook',
      icon: <FaFacebookF />,
      url: 'https://www.facebook.com/profile.php?id=100086048815176',
    },
  ],
  heroImage: '/assets/hero-img.webp',
};

const pageTitle = 'Robert Falkenberg | Author of Deceit Runs Red';
const pageDescription =
  'The official website of Robert Falkenberg, author of the debut legal thriller Deceit Runs Red. Explore the Sierra Justice Series, get updates, and join the newsletter.';
const canonicalUrl = 'https://www.robertfalkenberg.com/';

const jsonLdData = {
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: 'Robert Falkenberg',
  url: canonicalUrl,
  jobTitle: 'Author',
  alumniOf: 'Texas State University',
  description: pageDescription,
  image: `${canonicalUrl}assets/profile_img.webp`,
  worksFor: {
    '@type': 'Organization',
    name: 'Author',
  },
  sameAs: [
    'https://twitter.com/RobertFalkenbe6',
    'https://www.instagram.com/robertfalkenbergauthor/',
    'https://www.facebook.com/profile.php?id=100086048815176',
  ],
};

// ──────────────────────────────
// Animation variants
// ──────────────────────────────

const heroWordVariants = {
  hidden: { y: '100%' },
  visible: (i = 1) => ({
    y: 0,
    transition: { staggerChildren: 0.05, delayChildren: i * 0.1 },
  }),
};

const heroCharVariants = {
  hidden: { y: '100%', opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: 'spring', damping: 12, stiffness: 100 },
  },
};

const sectionRevealVariants = {
  hidden: { opacity: 0, y: 75 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

// ──────────────────────────────
// Small presentational helpers
// ──────────────────────────────

const AnimatedWord = ({ children }) => (
  <motion.div variants={heroWordVariants} className="hero-title-word">
    {children}
  </motion.div>
);

const AnimatedChar = ({ children }) => (
  <motion.span variants={heroCharVariants} className="hero-title-letter">
    {children}
  </motion.span>
);

const AnimatedSection = ({ children, className, id }) => (
  <motion.section
    id={id}
    className={className}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.01 }}
    variants={sectionRevealVariants}
  >
    {children}
  </motion.section>
);

const SeoHead = () => (
  <Helmet>
    <title>{pageTitle}</title>
    <meta name="description" content={pageDescription} />
    <link rel="canonical" href={canonicalUrl} />

    <meta property="og:title" content={pageTitle} />
    <meta property="og:description" content={pageDescription} />
    <meta property="og:url" content={canonicalUrl} />
    <meta property="og:type" content="website" />
    <meta property="og:image" content={`${canonicalUrl}assets/og-image.webp`} />

    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content={pageTitle} />
    <meta name="twitter:description" content={pageDescription} />
    <meta name="twitter:image" content={`${canonicalUrl}assets/og-image.webp`} />

    <script type="application/ld+json">{JSON.stringify(jsonLdData)}</script>
  </Helmet>
);

// ──────────────────────────────
// Awards highlight (right below hero)
// ──────────────────────────────

const AwardsSection = () => (
  <AnimatedSection className="content-wrapper awards-section" id="awards">
    <div className="award-card">
      <div className="award-header">
        <span className="award-label">Recognition</span>
      </div>

      <p className="award-quote-text">
        A gripping and entertaining, fast-paced adventure which makes for an excellent and entertaining read from the very first page. This book has everything a thriller needs—great pacing, multi-layered plot, expressive writing, believable characters, and plenty of action. The author is a master of dialogue and the narrative flows beautifully. This was a very easy book to read, with a tension-driven plot, skillful scene building, and an amazing setting. The descriptions are terrific—we’re transported to the Grand Canyon and are right there with the characters, experiencing every moment. This was a very enjoyable read and clearly well-researched. The cover is striking.
      </p>

      <p className="award-quote-source">
        Judge, 33rd Annual Writer’s Digest Self-Published Book Awards, Deceit
        Runs Red: Sierra Justice Series
      </p>
    </div>
  </AnimatedSection>
);

// ──────────────────────────────
// Hero Section
// ──────────────────────────────

// ──────────────────────────────
// Hero Section
// ──────────────────────────────

const HeroSection = React.memo(() => {
  const [currentTitle, setCurrentTitle] = React.useState(0);
  const [imgLoaded, setImgLoaded] = React.useState(false);
  const heroRef = React.useRef(null);
  const imgRef = React.useRef(null);

  const LQIP =
    'data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHZpZXdCb3g9JzAgMCAzMiAxOCcgd2lkdGg9JzMyJyBoZWlnaHQ9JzE4Jz4KICA8ZGVmcz4KICAgIDxsaW5lYXJHcmFkaWVudCBpZD0nZycgeDE9JzAnIHkxPScwJyB4Mj0nMCcgeTI9JzEnPgogICAgICA8c3RvcCBzdG9wLWNvbG9yPScjMGQwZDBkJyBvZmZzZXQ9JzAlJy8+CiAgICAgIDxzdG9wIHN0b3AtY29sb3I9JyMxMTExMTEnIG9mZnNldD0nNTAlJy8+CiAgICAgIDxzdG9wIHN0b3AtY29sb3I9JyMwMDAwMDAnIG9mZnNldD0nMTAwJScvPgogICAgPC9saW5lYXJHcmFkaWVudD4KICAgIDxmaWx0ZXIgaWQ9J2YnPjxmZUdhdXNzaWFuQmx1ciBzdGREZXZpYXRpb249JzInLz48L2ZpbHRlcj4KICA8L2RlZnM+CiAgPHJlY3Qgd2lkdGg9JzMyJyBoZWlnaHQ9JzE4JyBmaWxsPSd1cmwoI2cpJyBmaWx0ZXI9J3VybCgjZiknLz4KPC9zdmc+';

  const isHeroInView = useInView(heroRef, { amount: 0.1, once: false });
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  const contentOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0vh', '-30vh']);

  // Rotate subtitle while hero is in view
  React.useEffect(() => {
    let titleInterval;
    if (isHeroInView) {
      titleInterval = setInterval(() => {
        setCurrentTitle((prev) => (prev + 1) % authorData.titles.length);
      }, 2800);
    }
    return () => {
      if (titleInterval) clearInterval(titleInterval);
    };
  }, [isHeroInView]);

  // The hero image is <link rel="preload" fetchpriority="high"> in index.html and
  // loads eagerly below, so there's no JS fetch/loader gating it. If it's already
  // cached, `onLoad` can fire before React attaches its handler — reveal it right
  // away in that case, and hint the browser to prioritize its fetch.
  React.useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    el.setAttribute('fetchpriority', 'high');
    if (el.complete) setImgLoaded(true);
  }, []);

  const nameLines = authorData.name.split(' ');

  return (
    <header ref={heroRef} id="home" className="hero-section">
      <div className="hero-background">
        <div
          className="hero-placeholder"
          style={{ backgroundImage: `url(${LQIP})` }}
        />

        <img
          ref={imgRef}
          className={`hero-bg-img hero-bg-img--sharp${imgLoaded ? ' is-visible' : ''}`}
          src={authorData.heroImage}
          alt=""
          aria-hidden="true"
          decoding="async"
          loading="eager"
          draggable="false"
          onLoad={() => setImgLoaded(true)}
        />

        <div className="hero-overlay" />
      </div>

      <motion.div
        className="hero-content"
        style={{ opacity: contentOpacity, y: contentY }}
        variants={heroContentVariants}
        initial="hidden"
        animate="visible"
      >
        <h1 className="hero-main-title">
          {nameLines.map((line, lineIndex) => (
            <AnimatedWord key={lineIndex}>
              {line.split('').map((char, charIndex) => (
                <AnimatedChar key={`${char}-${charIndex}`}>
                  {char === ' ' ? '\u00A0' : char}
                </AnimatedChar>
              ))}
            </AnimatedWord>
          ))}
        </h1>

        <motion.div
          className="hero-footer"
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { delay: 0.6, duration: 0.8 },
            },
          }}
        >
          <div className="hero-subtitle-container">
            <AnimatePresence mode="wait">
              <motion.h2
                key={currentTitle}
                className="hero-subtitle"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
              >
                {authorData.titles[currentTitle]}
              </motion.h2>
            </AnimatePresence>
          </div>

          <div className="hero-socials">
            {authorData.socials.map((social, index) => (
              <motion.a
                key={index}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                aria-label={`Visit Robert Falkenberg on ${social.name}`}
                whileHover={{ y: -2, scale: 1.05 }}
                transition={{ type: 'spring', stiffness: 250, damping: 18 }}
              >
                {social.icon}
              </motion.a>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </header>
  );
});


// ──────────────────────────────
// Page
// ──────────────────────────────

export default function Homepage() {
  return (
    <div className="homepage-container">
      <SeoHead />

      <HeroSection />

      <main className="main-content">
        {/* NEW: Awards / Recognition block right below hero */}
        <AwardsSection />

        <AnimatedSection className="content-wrapper" id="books">
          <BookSection />
          {/* <UpcomingBookSection /> */}
        </AnimatedSection>

        <AnimatedSection className="content-wrapper alt-bg" id="about">
          <AboutSection />
        </AnimatedSection>

        <AnimatedSection className="content-wrapper" id="reviews">
          <ReviewTicker />
        </AnimatedSection>

        <AnimatedSection className="content-wrapper alt-bg" id="newsletter">
          <NewsletterSignup />
        </AnimatedSection>
      </main>
    </div>
  );
}
